/**
 * @file ClinicLandingView.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import React from "react";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import { Button, Dialog, DialogContent, Grid, Paper, Typography } from "@mui/material";
import "./AdminHome.css"
import { BackDropLoadingView } from "../../../utils/BackDropLoadingView";
import { ThemeProvider } from "@emotion/react";
import { noTextTransform, roboto700Text16Bold } from "../../../utils/theme/CommonTheme";
import { AppProgressBar } from "../../../utils/AppProgressBar";
import Snackbar from "@mui/material/Snackbar";
import { CSSTransition } from "react-transition-group";
import { AppAlert } from "../../../utils/AppAlert";
import { BaseComponent } from "../../base/BaseComponent";
import { GetClinicResponse } from "../../../../services/models/GetClinicResponse";
import { AdminViewModel } from "../viewmodels/AdminViewModel";
import { ApiError } from "../../../../services/models/ApiError";
import { ClinicHeaderView } from "./ClinicHeaderView";
import CustomClinicRows from "./CustomClinicRows";
import { NoDataFound } from "../../../common/customviews/NoDataFound";
import { RoleType } from "../../../login/utils/RoleType";
import { NewClinicView } from "./NewClinicView";
import { Clinic } from "../../../../services/models/Clinic";
import { BaseProps } from "../../base/BaseProps";
import { NoClinicFound } from "../../../utils/AppConstant";

/**
 * A type of Component state
 */
type CompState = {
    isNewClinicDialogOpen: boolean, mainLoadding: boolean, isDataLoaded: boolean,
    showSnackBar: boolean, snackBarErrorMessage: string, accessToken: string,
    reloadPage: boolean, enableNewClinic: boolean, successAlert: boolean
};


/**
 * A class definition for ClinicLandingView
 */
export class ClinicLandingView extends BaseComponent<BaseProps, CompState> {

    viewModel: AdminViewModel;
    constructor(props: BaseProps) {
        super(props)
        this.viewModel = new AdminViewModel();
        const accessToken = this.validateAndGetSession(ClinicLandingView.name);

        this.logger.warning(`AccessToken ${accessToken}`, ClinicLandingView.name);

        this.state = {
            isNewClinicDialogOpen: false,
            mainLoadding: false,
            isDataLoaded: false, showSnackBar: false, reloadPage: false,
            snackBarErrorMessage: "Unknown Error", accessToken: accessToken,
            enableNewClinic: this.getLoggedInUserRole().includes(RoleType.HCP) ? false : true, successAlert: false
        };

        this.getClinicResponse = new GetClinicResponse();
        this.getClinicResponse.rows = [];
    }

    getClinicResponse: GetClinicResponse;

    async componentDidMount(): Promise<void> {
        this.logger.verbose("ClinicLandingView (mounted)", ClinicLandingView.name);
        this.subscribeApiEvents();
        this.loadClinic(this.state.accessToken);
    }

    async componentWillUnmount(): Promise<void> {
        this.unSubscribeApiEvents();
    }

    /**
     * Subscribes to Api Events
     */
    private subscribeApiEvents = ()=> {
        this.viewModel.onClinicLoaded.subscribe((getClinicResponse : GetClinicResponse)=>{
            this.getClinicResponse = getClinicResponse;
            this.logger.verbose("Loaded Clinic ", ClinicLandingView.name);
            this.dataLoaded();
            this.setAllClinics(getClinicResponse.rows);
        });

        this.viewModel.onClinicError.subscribe((apiError: ApiError)=> {
            this.dataLoaded();
            this.hideMainLoading()
            this.showSnackBarError(apiError.message !== undefined ? apiError.message : "Error while loading clinics", false);
        })
    }

    /**
     * Unsubscribes to Api Events
     */
    private unSubscribeApiEvents = ()=> {
        this.viewModel.onClinicLoaded.unsubscribe();
        this.viewModel.onClinicError.unsubscribe();
    }

    /**
     * Loads the clinics
     * @param accessToken string 
     */
    private loadClinic = (accessToken: string)=> { // eslint-disable-line
        this.logger.verbose("loadClinic()", ClinicLandingView.name);
        this.dataReLoad();
        // Load clinics
        this.viewModel.getClinics(accessToken); // eslint-disable-line
    }

    /**
     * Dismisses the new clinic dialog
     */
    dismissNewClinicDialog = () => {
        this.setState({ isNewClinicDialogOpen: false });
    }

    /**
     * Shows the new clinic dialog
     */
    private showDialog = () => {
        this.setState({ isNewClinicDialogOpen: true });
    }

    /**
     * Sets the state to reloads the data
     */
    private dataReLoad = () => {
        this.setState({ isDataLoaded: false });
    }

    /**
     * Sets the state to load the data
     */
    private dataLoaded = () => {
        this.setState({ isDataLoaded: true });
    }

    /**
     * Hides mainloading
     */
    private hideMainLoading = () => {
        this.setState({ mainLoadding: false });
    }

    /**
     * Hides snack bar error
     */
    private hideSnackBarError = () => {
        this.setState({ showSnackBar: false})
    }

    /**
     * Shows snack bar error
     * @param message string
     */
    private showSnackBarError = (message: string, isSucessAlert: boolean) => {
        this.setState({ showSnackBar: true, snackBarErrorMessage: message, successAlert: isSucessAlert})
    }

    /**
     * Handles backdrop close
     */
    private handleBackDropClose = () => {
        this.setState({ mainLoadding: false })
    };

    /**
     * Updates
     */
    private updateClinicList = (clinic: Clinic) =>{
        const clinics: Clinic[] = JSON.parse(this.getAllClinics());
        clinics.push(clinic);
        this.setAllClinics(clinics);
        this.getClinicResponse.rows = clinics;
    }

    /**
     * Handles new clinic callback
     */
    private onNewClinicCreated = (clinic: Clinic)=> {
        this.updateClinicList(clinic);
        this.dismissNewClinicDialog();
        this.showSnackBarError("Clinic is created sucessfully.", true);
        this.loadClinic(this.state.accessToken);
    }

    render() {

        if (this.state.isDataLoaded) {
            this.logger.verbose("dataLoaded", ClinicLandingView.name);
            return <React.Fragment>
                <Grid className="AdminTableBox">
                    <Grid container spacing={0} >
                        <Grid item xs={5} container>
                            <ThemeProvider theme={roboto700Text16Bold}>
                                <Typography className="ClinicHeader"> Clinics ({this.getClinicResponse?.rows?.length})</Typography>
                            </ThemeProvider>
                        </Grid>
                        <Grid item xs={7} container justifyContent="flex-end">
                            <ThemeProvider theme={noTextTransform}>
                                { this.state.enableNewClinic  && <Button onClick={this.showDialog} size="small" variant="contained"
                                    disableElevation  className="AdminAddNewRecordButton">
                                    + &nbsp;&nbsp;  Add a Clinic
                                </Button>}
                            </ThemeProvider>
                        </Grid>
                    </Grid>

                    {this.getClinicResponse?.rows?.length == 0 && <NoDataFound message={NoClinicFound} />}
                    {// eslint-disable-next-line
                        (this.getClinicResponse?.rows!!.length  > 0) && <TableContainer className="ClinicTableContainer" component={Paper} sx={{ width: '100%' }}>
                            <Table aria-label="custom pagination table">
                                <ClinicHeaderView />
                                <CustomClinicRows getClinicResponse={this.getClinicResponse!} onClinicEdited={() => {
                                    this.loadClinic(this.state.accessToken);
                                }} />
                            </Table>
                        </TableContainer>}
                    <Dialog onClose={this.dismissNewClinicDialog} className="AddNewClinicDialogContainer" aria-labelledby="customized-dialog-title" open={this.state.isNewClinicDialogOpen} fullWidth={true}
                        maxWidth="lg" disableScrollLock={true} PaperProps={{ sx: { borderRadius: "1.25rem", maxWidth:"50%" } }}>
                        <DialogContent className="AddNewClinicDialogBox" style={{ justifyContent: "center", alignItems: "center", overflow: "hidden" }}>
                            <NewClinicView onDialogDismissed={this.dismissNewClinicDialog} onNewClinicCreated={this.onNewClinicCreated} />
                        </DialogContent>
                    </Dialog>
                </Grid >
                <CSSTransition
                    in={this.state.showSnackBar}
                    timeout={300}
                    classNames="alert"
                    unmountOnExit>
                    <Snackbar open={this.state.showSnackBar} autoHideDuration={6000} onClose={this.hideSnackBarError}
                        anchorOrigin={{ vertical: "top", horizontal: "center" }} >
                        <AppAlert onClose={this.hideSnackBarError} severity={this.state.successAlert ? "success" :"error"} sx={{ width: "150%" }}>
                            {`${this.state.snackBarErrorMessage}`}
                        </AppAlert>
                    </Snackbar>
                </CSSTransition>
                <BackDropLoadingView handleBackDropClose={this.handleBackDropClose} isLoading={this.state.mainLoadding} />
            </React.Fragment>
        } else {
            return <div className="AdminLoadingBox">
                <AppProgressBar color="primary" />
            </div>
        }
    }
}
